import { useMemo, useState } from 'react';
import { moment } from '@hotjar/insights-common/lib/moment';
import { StatefulFilterChain as StatefulFilterChainFromPackage } from '@hotjar/global-filters';
import { segment as trackingSegment } from '@hotjar/tracking';
import { ErrorBoundary } from '@hotjar/error-handler';
import type {
  AddFilterTippyInstance,
  FilterKindEnum,
  GlobalFilter,
  MinimalDateConstraint,
  ContextOfUse,
} from '@hotjar/global-filters';
import { useIntegrationTokens } from '@hotjar/integrations';
import type { Segment } from '@hotjar/webapp/features/Recordings/types';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { isAllowedForAsk } from '@hotjar/feedback/domain/common/Feature/feature';
import { isAllowed, getMinimumFeatureGroupForFeature, useFeatureFlag } from '@hotjar/feature-flags';
import { useCurrentSite } from '@hotjar/site';
import { SegmentEditCreate } from '@hotjar/webapp/features/Segments/SegmentEditCreate';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { isSiteOnScaleOrScaleTrial } from '@hotjar/recordings/src/utils/isSiteOnScaleOrScaleTrial';
import { useAccountTrialInfo } from '@hotjar/account';
import { isOnActiveOrEligibleTrial } from '@hotjar/subscription-management';

export const MAX_DATA_RETENTION_PERIOD: MinimalDateConstraint = {
  minDate: moment().subtract(1, 'year').toDate(),
  reason: 'Exceeds data retention period (12 months).',
};

const quantzMinDate = new Date('2022-03-08T00:00:00.000Z');
const nonBreakingSpace = '\u00A0';

export const START_DATE_OF_QUANTZ_DATA_COLLECTION: MinimalDateConstraint = {
  minDate: quantzMinDate,
  reason: `Data is only available from ${moment(quantzMinDate)
    .format('MMMM D')
    .replace(' ', nonBreakingSpace)}.`,
  link: {
    text: 'Why is this?',
    href: '/articles/5450008026007#dashboard-data',
  },
};

export type StatefulFilterChainProps = {
  currentFiltersInUiFormat?: GlobalFilter;
  currentSegment?: Segment;
  segments?: Segment[];
  onChange: (filtersInUiFormat: GlobalFilter, segmentId?: number) => void;
  onSegmentSaved?: (segment: Segment) => void;
  onUpsellRequest: (featureName: string, kind?: string) => void;
  setAddFilterTippyInstance?: (tippyInstance: AddFilterTippyInstance | null) => void;
  hideSaveSegment?: boolean;
  hideAllFilters?: boolean;
  hideDateRangeFilter?: boolean;
  usePrimaryStyledAddFilters?: boolean;
  useToggleGroupDatePicker?: boolean;
  hideAllDateOption?: boolean;
  dateConstraint?: MinimalDateConstraint;
  supportedFilterList: Set<FilterKindEnum>;
  contextOfUse: ContextOfUse;
  isFiltersDropdownVisibleByDefault?: boolean;
  doNotTrackFiltersChange?: boolean;
};

export const StatefulFilterChain = ({
  currentFiltersInUiFormat,
  currentSegment,
  segments = [],
  onChange,
  onSegmentSaved,
  onUpsellRequest,
  setAddFilterTippyInstance,
  hideSaveSegment,
  hideAllFilters,
  hideDateRangeFilter,
  usePrimaryStyledAddFilters,
  useToggleGroupDatePicker,
  hideAllDateOption,
  dateConstraint,
  supportedFilterList,
  contextOfUse,
  isFiltersDropdownVisibleByDefault,
  doNotTrackFiltersChange,
}: StatefulFilterChainProps): JSX.Element => {
  const [editedSegment, setEditedSegment] = useState<Segment | undefined>();
  const { data: currentSite, isError: currentSiteIsError } = useCurrentSite();
  const { click, getGroupDataForSite, getPermissionLevelForSite } = useTrack();
  const { coveragePercentage } = useTrafficCoverage();
  const hasSegmentsCreatePermission = usePermission('segments-create');
  /**
   * TODO: Refactor logic in RECS-1380 to be self-contained in hook
   */
  const trialInfo = useAccountTrialInfo();
  const isNewScaleEnabled = useFeatureFlag('billing_new_scale');
  const isSiteInScaleTrialCohort =
    useFeatureFlag('scale_trial_cohort') && currentSite?.effectivePlan === 'business';
  const currentSiteWithProperTrialState = currentSite
    ? {
        ...currentSite,
        trial: {
          ...currentSite.trial,
          active: isOnActiveOrEligibleTrial(trialInfo),
        },
      }
    : null;
  const isScaleOrScaleTrial = currentSiteWithProperTrialState
    ? isSiteOnScaleOrScaleTrial(currentSiteWithProperTrialState)
    : false;
  // This check is the same we do in AsyncTrendsPage and AsyncDashboardQuantzPage
  const isScaleTrial =
    (isNewScaleEnabled && isSiteInScaleTrialCohort) ||
    (!isNewScaleEnabled &&
      !isOnActiveOrEligibleTrial(trialInfo) &&
      currentSiteWithProperTrialState?.effectivePlan === 'business');

  const globalFilterContextValue = useMemo(
    () => ({
      useTrackResult: {
        click,
        getGroupDataForSite,
        getPermissionLevelForSite,
        track: trackingSegment.track,
      },
      useTrafficCoverageResult: { coveragePercentage },
      isAllowed,
      getMinimumFeatureGroupForFeature,
      isAllowedForAsk,
      hasFilterSavePermission: hasSegmentsCreatePermission ?? false,
      useFeatureFlag,
      useIntegrationTokens,
    }),
    [
      click,
      coveragePercentage,
      getGroupDataForSite,
      getPermissionLevelForSite,
      hasSegmentsCreatePermission,
    ],
  );

  return (
    <ErrorBoundary squadSentryTag="product-foundations" featureName="global.filters">
      <StatefulFilterChainFromPackage
        currentFiltersInUiFormat={currentFiltersInUiFormat}
        currentSegment={currentSegment}
        currentSite={currentSite}
        currentSiteIsError={currentSiteIsError}
        segments={segments}
        onChange={onChange}
        onUpsellRequest={onUpsellRequest}
        setAddFilterTippyInstance={setAddFilterTippyInstance}
        onSegmentEdited={setEditedSegment}
        hideSaveSegment={hideSaveSegment}
        hideAllFilters={hideAllFilters}
        hideDateRangeFilter={hideDateRangeFilter}
        usePrimaryStyledAddFilters={usePrimaryStyledAddFilters}
        useToggleGroupDatePicker={useToggleGroupDatePicker}
        hideAllDateOption={hideAllDateOption}
        dateConstraint={dateConstraint}
        supportedFilterList={supportedFilterList}
        contextOfUse={contextOfUse}
        isFiltersDropdownVisibleByDefault={isFiltersDropdownVisibleByDefault}
        doNotTrackFiltersChange={doNotTrackFiltersChange}
        isScaleOrScaleTrial={isScaleOrScaleTrial}
        isScaleTrial={isScaleTrial}
        contextObject={globalFilterContextValue}
      />
      {editedSegment && currentSite && (
        <SegmentEditCreate
          isOpen
          onClose={() => setEditedSegment(undefined)}
          onSegmentChange={(segment) => onSegmentSaved?.(segment)}
          segment={editedSegment}
          currentSite={currentSite}
          onUpsellRequest={onUpsellRequest}
        />
      )}
    </ErrorBoundary>
  );
};
